import React, { useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useCoins } from '../../context/CoinContext';
import { generateUpgradeOffer, generateAdditionalViewOffer, purchaseAdditionalViews } from '../../utils/imageViewTracking';
import { doc, updateDoc, arrayUnion } from 'firebase/firestore';
import { db } from '../../firebase/firebaseConfig';

const ImageViewLimitModal = ({ 
  isOpen, 
  onClose, 
  userData, 
  currentViewCount, 
  onUpgradeSuccess,
  onPurchaseSuccess 
}) => {
  const { currentUser } = useAuth();
  const { coins } = useCoins();
  const [isProcessing, setIsProcessing] = useState(false);
  const [selectedOffer, setSelectedOffer] = useState(null);
  const [showPurchaseForm, setShowPurchaseForm] = useState(false);
  const [additionalViews, setAdditionalViews] = useState(100);

  if (!isOpen) return null;

  const upgradeOffer = generateUpgradeOffer(userData);
  const additionalViewOffer = generateAdditionalViewOffer(additionalViews);

  const handleUpgrade = async () => {
    if (!upgradeOffer || !currentUser) return;

    setIsProcessing(true);
    try {
      const userRef = doc(db, 'users', currentUser.uid);
      await updateDoc(userRef, {
        subscription: upgradeOffer.target,
        subscriptionUpdatedAt: new Date().toISOString(),
        coins: coins - upgradeOffer.discountedPrice,
        purchaseHistory: arrayUnion({
          type: 'subscriptionUpgrade',
          from: userData.subscription,
          to: upgradeOffer.target,
          price: upgradeOffer.discountedPrice,
          timestamp: new Date().toISOString()
        })
      });

      alert(`อัพเกรดเป็น ${upgradeOffer.target} สำเร็จ!`);
      onUpgradeSuccess();
      onClose();
    } catch (error) {
      console.error('Error upgrading subscription:', error);
      alert('เกิดข้อผิดพลาดในการอัพเกรด');
    } finally {
      setIsProcessing(false);
    }
  };

  const handlePurchaseAdditionalViews = async () => {
    if (!currentUser) return;

    setIsProcessing(true);
    try {
      await purchaseAdditionalViews(currentUser.uid, additionalViews, additionalViewOffer.offerPrice);
      alert(`ซื้อสิทธิ์การดูรูปเพิ่มเติม ${additionalViews} ครั้งสำเร็จ!`);
      onPurchaseSuccess();
      onClose();
    } catch (error) {
      console.error('Error purchasing additional views:', error);
      alert('เกิดข้อผิดพลาดในการซื้อสิทธิ์เพิ่มเติม');
    } finally {
      setIsProcessing(false);
    }
  };

  const getSubscriptionDisplayName = (subscription) => {
    const names = {
      free: 'Free',
      basic: 'Basic',
      silver: 'Silver',
      gold: 'Gold',
      platinum: 'Platinum',
      premium: 'Premium',
      vip: 'VIP',
      starter: 'Starter',
      pro: 'Pro',
      enterprise: 'Enterprise',
      ultraEnterprise: 'Ultra Enterprise'
    };
    return names[subscription] || subscription;
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-bold">📸 สิทธิ์การดูรูปภาพหมดแล้ว</h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 text-2xl"
          >
            ×
          </button>
        </div>

        <div className="mb-6">
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4">
            <div className="flex items-center">
              <span className="text-yellow-600 text-2xl mr-3">⚠️</span>
              <div>
                <h3 className="text-yellow-800 font-semibold">สิทธิ์การดูรูปภาพหมดแล้ว</h3>
                <p className="text-yellow-700 text-sm mt-1">
                  คุณใช้สิทธิ์การดูรูปภาพครบแล้วในวันนี้ ({currentViewCount} ครั้ง)
                </p>
              </div>
            </div>
          </div>

          <div className="text-center mb-6">
            <p className="text-gray-600 mb-4">เลือกวิธีเพิ่มสิทธิ์การดูรูปภาพ:</p>
          </div>
        </div>

        {/* Upgrade Offer */}
        {upgradeOffer && (
          <div className="mb-6">
            <div className="bg-gradient-to-r from-blue-50 to-purple-50 border border-blue-200 rounded-lg p-4">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-lg font-semibold text-blue-900">🚀 อัพเกรด Package</h3>
                <span className="bg-green-100 text-green-800 px-2 py-1 rounded-full text-sm font-medium">
                  ประหยัด {upgradeOffer.savings} เหรียญ
                </span>
              </div>
              
              <div className="mb-4">
                <p className="text-blue-800 mb-2">
                  อัพเกรดจาก <strong>{getSubscriptionDisplayName(userData.subscription)}</strong> เป็น{' '}
                  <strong>{getSubscriptionDisplayName(upgradeOffer.target)}</strong>
                </p>
                <div className="flex items-center space-x-4 text-sm">
                  <span className="text-blue-700">
                    📈 สิทธิ์การดูรูป: {upgradeOffer.views === -1 ? 'ไม่จำกัด' : `${upgradeOffer.views} ครั้ง/วัน`}
                  </span>
                </div>
              </div>

              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <span className="text-lg font-bold text-blue-900">
                    ฿{upgradeOffer.discountedPrice}
                  </span>
                  {upgradeOffer.discount > 0 && (
                    <span className="text-sm text-gray-500 line-through">
                      ฿{upgradeOffer.originalPrice}
                    </span>
                  )}
                </div>
                <button
                  onClick={handleUpgrade}
                  disabled={isProcessing || coins < upgradeOffer.discountedPrice}
                  className={`px-6 py-2 rounded-lg font-medium transition ${
                    coins >= upgradeOffer.discountedPrice && !isProcessing
                      ? 'bg-blue-500 text-white hover:bg-blue-600'
                      : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  }`}
                >
                  {isProcessing ? 'กำลังอัพเกรด...' : 'อัพเกรดเลย'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Additional Views Offer */}
        <div className="mb-6">
          <div className="bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-lg p-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-lg font-semibold text-green-900">⚡ ซื้อสิทธิ์เพิ่มเติม</h3>
              <span className="bg-red-100 text-red-800 px-2 py-1 rounded-full text-sm font-medium">
                ลดราคา {additionalViewOffer.discount}%
              </span>
            </div>
            
            <div className="mb-4">
              <p className="text-green-800 mb-2">
                ซื้อสิทธิ์การดูรูปภาพเพิ่มเติม {additionalViews} ครั้ง
              </p>
              <div className="text-sm text-green-700">
                <p>⏰ ข้อเสนอพิเศษนี้มีอายุ 24 ชั่วโมง</p>
                <p>💰 ประหยัด {additionalViewOffer.savings} เหรียญจากราคาปกติ</p>
              </div>
            </div>

            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <span className="text-lg font-bold text-green-900">
                  ฿{additionalViewOffer.offerPrice}
                </span>
                <span className="text-sm text-gray-500 line-through">
                  ฿{additionalViewOffer.originalPrice}
                </span>
              </div>
              <button
                onClick={handlePurchaseAdditionalViews}
                disabled={isProcessing || coins < additionalViewOffer.offerPrice}
                className={`px-6 py-2 rounded-lg font-medium transition ${
                  coins >= additionalViewOffer.offerPrice && !isProcessing
                    ? 'bg-green-500 text-white hover:bg-green-600'
                    : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                }`}
              >
                {isProcessing ? 'กำลังซื้อ...' : 'ซื้อเลย'}
              </button>
            </div>
          </div>
        </div>

        {/* Custom Additional Views */}
        <div className="mb-6">
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-3">🎯 เลือกจำนวนเอง</h3>
            
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                จำนวนการดูรูปภาพเพิ่มเติม
              </label>
              <div className="flex items-center space-x-2">
                <input
                  type="number"
                  value={additionalViews}
                  onChange={(e) => setAdditionalViews(parseInt(e.target.value) || 100)}
                  min="10"
                  max="1000"
                  className="flex-1 p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                <span className="text-sm text-gray-600">ครั้ง</span>
              </div>
            </div>

            <div className="text-sm text-gray-600 mb-4">
              <p>ราคาปกติ: ฿{Math.round(additionalViews * 4.99)}</p>
              <p>ราคาพิเศษ: ฿{Math.round(additionalViews * 0.99)}</p>
              <p>ประหยัด: ฿{Math.round(additionalViews * 4)}</p>
            </div>

            <button
              onClick={() => setShowPurchaseForm(true)}
              className="w-full px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition"
            >
              คำนวณราคาและซื้อ
            </button>
          </div>
        </div>

        {/* Current Status */}
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
          <h3 className="text-lg font-semibold text-gray-900 mb-2">📊 สถานะปัจจุบัน</h3>
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <p className="text-gray-600">Package ปัจจุบัน:</p>
              <p className="font-semibold">{getSubscriptionDisplayName(userData.subscription)}</p>
            </div>
            <div>
              <p className="text-gray-600">เหรียญที่มี:</p>
              <p className="font-semibold">{coins} เหรียญ</p>
            </div>
            <div>
              <p className="text-gray-600">ดูรูปวันนี้:</p>
              <p className="font-semibold">{currentViewCount} ครั้ง</p>
            </div>
            <div>
              <p className="text-gray-600">สิทธิ์การดู:</p>
              <p className="font-semibold">
                {userData.subscription === 'platinum' || userData.subscription === 'vip' || userData.subscription === 'ultraEnterprise' 
                  ? 'ไม่จำกัด' 
                  : `${upgradeOffer?.dailyLimit || 0} ครั้ง/วัน`}
              </p>
            </div>
          </div>
        </div>

        <div className="mt-6 flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition"
          >
            ปิด
          </button>
        </div>
      </div>
    </div>
  );
};

export default ImageViewLimitModal;
